import { useEffect, useRef, useCallback, useState } from "react";
import openSocket from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "react-query";

import {
  CancelX,
  ChartPieSliceTitleIcon,
  Elipses,
  LightElipses,
  NotificationIcon,
  OpenVolume,
  CloseVolume,
} from "./Icons";
import { useAuth } from "../hooks/useAuth";
import { fetchActionsUtil } from "../utils/helpers";
import { useCacheBuster } from "react-cache-buster";
import { toast } from "react-toastify";

const SOCKET_EVENTS = {
  STAFF: "sendtostaff",
  CUSTOMER: "sendtocustomer",
};

const socketType = {
  NOTIFICATION: "notification",
};

const NOTIF_KEY = ["notifications"];

function playSound() {
  const audio = new Audio("/sound.wav");
  audio.play().catch((err) => console.warn("Audio blocked:", err));
}

function parseUTCDate(dateStr) {
  if (!dateStr) return null;
  const normalized = dateStr.toString().replace(" ", "T");
  const str = normalized.endsWith("Z") ? normalized : `${normalized}Z`;
  return new Date(str);
}

export const Notification = ({ Cust_ID, backendUrl, Staff_ID, sound }) => {
  const { checkCacheStatus } = useCacheBuster();
  const queryClient = useQueryClient();
  const socketRef = useRef(null);
  const soundRef = useRef(sound);
  const prevCountRef = useRef(0);
  const timerRef = useRef(null);

  const [openNotifications, setOpenNotifications] = useState(false);

  useEffect(() => {
    soundRef.current = sound;
  }, [sound]);

  useEffect(() => {
    const id = Staff_ID || Cust_ID;
    if (!id || !backendUrl) return;

    const socket = openSocket(backendUrl, {
      transports: ["websocket"],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Socket connected succesfully 1s:", socket.id);
      checkCacheStatus();
    });

    socket.on("disconnect", (reason) => {
      console.warn("⚠️ Socket disconnected:", reason);
    });

    const event = Cust_ID
      ? `${SOCKET_EVENTS.CUSTOMER}-${Cust_ID}`
      : `${SOCKET_EVENTS.STAFF}-${Staff_ID}`;

    const handleIncoming = (payload) => {
      const incomingNotification =
        payload?.notification ?? payload?.newObj ?? null;

      if (incomingNotification) {
        queryClient.setQueryData(NOTIF_KEY, (old = []) => [
          incomingNotification,
          ...old.filter((item) => item.id !== incomingNotification.id),
        ]);
      }

      queryClient.invalidateQueries({ queryKey: NOTIF_KEY });
    };

    socket.on(event, handleIncoming);

    return () => {
      socket.off(event, handleIncoming);
      socket.disconnect();
    };
  }, [backendUrl, Staff_ID, Cust_ID, queryClient, checkCacheStatus]);

  const { data: notifications = [], refetch, isFetching } = useQuery({
    queryKey: NOTIF_KEY,
    queryFn: async () => {
      const res = await fetchActionsUtil(
        `${backendUrl}/api/notifications?limit=100`,
        "GET",
      );
      return res?.data ?? [];
    },
    enabled: !!(Staff_ID || Cust_ID),
    refetchOnWindowFocus: false,
  });

  const unreadCount = notifications.filter((n) => !n.readAt).length;
  const badgeCount = unreadCount > 9 ? "9+" : String(unreadCount);
  const showBadge = unreadCount > 0;

  useEffect(() => {
    if (unreadCount > prevCountRef.current && soundRef.current) {
      playSound();
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  const readAllMutation = useMutation({
    mutationFn: () =>
      fetchActionsUtil(`${backendUrl}/api/notifications/read-all`, "PATCH"),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIF_KEY });
      const previous = queryClient.getQueryData(NOTIF_KEY);
      queryClient.setQueryData(NOTIF_KEY, (old = []) =>
        old.map((n) => ({
          ...n,
          readAt: n.readAt ?? new Date().toISOString(),
        })),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIF_KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_KEY });
    },
  });

  const openPanel = useCallback(() => {
    setOpenNotifications(true);
    if (unreadCount > 0) {
      timerRef.current = setTimeout(() => {
        readAllMutation.mutate();
      }, 5000);
    }
  }, [readAllMutation, unreadCount]);

  const closePanel = useCallback(() => {
    setOpenNotifications(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const id = Staff_ID || Cust_ID;
    if (!id || !backendUrl) return;

    const socket = openSocket(backendUrl, {
      transports: ["websocket"],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Socket connected:", socket.id);
      checkCacheStatus();
    });

    socket.on("disconnect", (reason) => {
      console.warn("⚠️ Socket disconnected:", reason);
    });

    const event = Cust_ID
      ? `${SOCKET_EVENTS.CUSTOMER}-${Cust_ID}`
      : `${SOCKET_EVENTS.STAFF}-${Staff_ID}`;

    const handleIncoming = (payload) => {
      const incomingNotification =
        payload?.notification ?? payload?.newObj ?? null;

      if (incomingNotification) {
        queryClient.setQueryData(NOTIF_KEY, (old = []) => [
          incomingNotification,
          ...old.filter((item) => item.id !== incomingNotification.id),
        ]);
      }

      queryClient.invalidateQueries({ queryKey: NOTIF_KEY });
    };

    socket.on(event, handleIncoming);

    return () => {
      socket.off(event, handleIncoming);
      socket.disconnect();
    };
  }, [backendUrl, Staff_ID, Cust_ID, queryClient, checkCacheStatus]);

  return (
    <>
      <div className="p-cursor" onClick={openPanel}>
        <div className="notify-display">
          <NotificationIcon />
          {showBadge && (
            <div className="notify-display-inside">
              <p>{badgeCount}</p>
            </div>
          )}
        </div>
      </div>

      <NotificationDisplay
        onHide={closePanel}
        notifications={notifications}
        openNotifications={openNotifications}
        Staff_ID={Staff_ID}
        backendUrl={backendUrl}
        Cust_ID={Cust_ID}
        refetch={refetch}
        isFetching={isFetching}
      />
    </>
  );
};

const NotificationDisplay = ({
  onHide,
  notifications,
  openNotifications,
  Staff_ID,
  backendUrl,
  Cust_ID,
  refetch,
  isFetching,
}) => {
  const { setUser, user: authUser } = useAuth();
  const queryClient = useQueryClient();

  const changeSound = async () => {
    const res = await fetchActionsUtil(
      `${backendUrl}/api/users/Sound/${authUser?.Staff_ID}`,
      "POST",
    );
    if (res.message === "Successful") {
      if (!authUser?.sound) playSound();
      setUser({ ...authUser, sound: !authUser?.sound });
    }
  };

  const clearAllMutation = useMutation({
    mutationFn: () =>
      fetchActionsUtil(`${backendUrl}/api/notifications`, "DELETE"),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIF_KEY });
      const previous = queryClient.getQueryData(NOTIF_KEY);
      queryClient.setQueryData(NOTIF_KEY, []);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIF_KEY, context.previous);
      toast.error("Failed to clear notifications");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_KEY });
    },
  });

  return (
    <div
      className={`notification overflow-hidden ${
        openNotifications ? "visible" : "hidden"
      }`}
    >
      <div className="border-bottom p-4">
        <div className="d-flex justify-content-between ">
          <div className="noti-text">Notification</div>
          <div className="d-flex align-items-baseline gap-3">
            {Staff_ID && (
              <div className="p-cursor" onClick={changeSound}>
                {authUser?.sound ? <CloseVolume /> : <OpenVolume />}
              </div>
            )}
            <div className="p-cursor" onClick={onHide}>
              <CancelX />
            </div>
          </div>
        </div>
        <button
          className="text-primary bg-transparent mr-auto"
          onClick={() => clearAllMutation.mutate()}
          disabled={clearAllMutation.isLoading || !notifications?.length}
        >
          Clear All
        </button>
      </div>

      <div className="notification-scroll">
        <Messages
          messageData={notifications}
          backendUrl={backendUrl}
          Cust_ID={Cust_ID}
          onHide={onHide}
          refetch={refetch}
          isFetching={isFetching}
        />
      </div>
    </div>
  );
};

const Messages = ({
  messageData,
  backendUrl,
  Cust_ID,
  onHide,
  refetch,
  isFetching,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const readMutation = useMutation({
    mutationFn: (id) =>
      fetchActionsUtil(`${backendUrl}/api/notifications/${id}/read`, "PATCH"),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: NOTIF_KEY });
      const previous = queryClient.getQueryData(NOTIF_KEY);
      queryClient.setQueryData(NOTIF_KEY, (old = []) =>
        old.map((n) =>
          n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIF_KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_KEY });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (id) =>
      fetchActionsUtil(`${backendUrl}/api/notifications/${id}`, "DELETE"),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: NOTIF_KEY });
      const previous = queryClient.getQueryData(NOTIF_KEY);
      queryClient.setQueryData(NOTIF_KEY, (old = []) =>
        old.filter((n) => n.id !== id),
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous)
        queryClient.setQueryData(NOTIF_KEY, context.previous);
      toast.error("Failed to delete notification");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIF_KEY });
      toast.success("Notification deleted");
    },
  });

  const navigateIt = (item) => {
    if (!item.readAt) readMutation.mutate(item.id);
    const route = item?.data?.route;
    const page = item?.data?.page || "/dashboard";

    if (route || page) navigate(route || page, { replace: true });
    console.log(item);
    onHide();
  };

  if (!messageData?.length) {
    return (
      <div className="d-flex p-4 flex-column align-items-center h-100 text-center justify-content-center text-muted">
        <p>No notifications yet</p>
      </div>
    );
  }

  return messageData.map((el, i) => {
    const parsedDate = new Date(el.createdAt);

    // console.log("================================");
    // console.log("Notification ID:", el.id);
    // console.log("Raw createdAt:", el.createdAt);
    // console.log("Parsed:", parsedDate);
    // console.log("Parsed ISO:", parsedDate.toISOString());
    // console.log("Now:", new Date());
    // console.log("Now ISO:", new Date().toISOString());
    // console.log(
    //   "Diff (minutes):",
    //   (parsedDate.getTime() - Date.now()) / (1000 * 60),
    // );
    // console.log(
    //   "Distance:",
    //   formatDistanceToNowStrict(parsedDate, {
    //     addSuffix: true,
    //   }),
    // );
    // console.log("================================");

    return (
      <div
        className={`d-flex p-4 border-bottom ${el.readAt ? "" : "read"}`}
        key={el.id ?? i}
      >
        <div className="icon mt-2">
          <ChartPieSliceTitleIcon />
        </div>

        <div className="grow-it p-cursor" onClick={() => navigateIt(el)}>
          <div className="d-flex justify-content-between pb-2">
            <p className="fw-bold m-0 p-0">{el.title}</p>

            <div className="d-flex gap-1">
              <p className="text-muted my-0 p-0 text-nowrap">
                {formatDistanceToNowStrict(parsedDate, {
                  addSuffix: true,
                })}
              </p>
            </div>
          </div>

          <div className="d-flex justify-content-between inside-text">
            <p
              className="text-dark"
              dangerouslySetInnerHTML={{ __html: el.body }}
            />
            <span className="mx-2">
              {el.readAt ? <LightElipses /> : <Elipses />}
            </span>
          </div>
        </div>
      </div>
    );
  });
};
